/**
 * Copyright (c) Microsoft. All rights reserved.
 */

import { ActionButton, IButtonProps } from '@fluentui/react/lib/Button';
import { Stack } from '@fluentui/react/lib/Stack';
import { ITextProps, Text } from '@fluentui/react/lib/Text';
import { Property } from 'csstype';
import * as React from 'react';
import { defineMessages } from 'react-intl';
import { useLocalization } from 'src/core/hooks/useLocalization';
import { getStylist } from 'src/core/themes/luisStylist';

export type FiltersHeaderProps = {
    /**
     * Handler function for dismiss
     */
    onDismissClick: () => void;

    /**
     * Handler function for clear all filters button
     */
    onClearAllClick: () => void;

    /**
     * Indicates of the clear all button is disabled.
     */
    clearAllDisabled?: boolean;

    /**
     * The header text
     */
    title?: string;
};

const messages = defineMessages({
    allFilters: {
        id: 'core.filtersHeader.allFilters',
        defaultMessage: 'All filters'
    },
    clearAllFilters: {
        id: 'core.filtersHeader.clearAllFilters',
        defaultMessage: 'Clear all'
    }
});

const { styleComponent } = getStylist('Filters');

const ModelFiltersHeader = styleComponent<ITextProps>(Text)('ModelFiltersHeader', theme => ({
    height: theme.headerHeight,
    fontWeight: theme.headerFontWeight as Property.FontWeight
}));

const Button = styleComponent<IButtonProps>(ActionButton)('Button', theme => ({
    padding: 0,
    marginBottom: theme.buttonMargin
}));

/**
 * Represents filters blade header
 */
export const FiltersHeader = (props: FiltersHeaderProps) => {
    const { title, onDismissClick, onClearAllClick, clearAllDisabled = false } = props;
    const { localize } = useLocalization();

    return (
        <>
            {title && <ModelFiltersHeader variant="xxLarge">{title}</ModelFiltersHeader>}
            <Stack horizontal horizontalAlign="space-between" verticalAlign="center">
                <Button iconProps={{ iconName: 'ReplyAlt' }} text={localize(messages.allFilters)} onClick={onDismissClick} />
                <Button
                    iconProps={{ iconName: 'ClearFilter' }}
                    disabled={clearAllDisabled}
                    text={localize(messages.clearAllFilters)}
                    onClick={onClearAllClick}
                />
            </Stack>
        </>
    );
};
